import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/Button";
import { Input, Textarea } from "@/components/ui/Input";
import { Card, SectionLabel } from "@/components/ui/Card";
import type { BackCheckinRequest } from "@/types";

interface Props {
  onSubmit: (payload: BackCheckinRequest) => Promise<void> | void;
  submitting?: boolean;
  defaultDate?: string;
}

export function BackCheckInForm({ onSubmit, submitting, defaultDate }: Props) {
  const { t } = useTranslation();
  const today = defaultDate || new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [stiffness, setStiffness] = useState(0);
  const [pain, setPain] = useState(0);
  const [location, setLocation] = useState<string>("");
  const [radiating, setRadiating] = useState(false);
  const [numbness, setNumbness] = useState(false);
  const [afterYesterday, setAfterYesterday] = useState(false);
  const [sleep, setSleep] = useState(3);
  const [stress, setStress] = useState(3);
  const [notes, setNotes] = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    await onSubmit({
      date,
      morning_stiffness_minutes: stiffness,
      pain_score: pain,
      location: location || null,
      radiating,
      numbness,
      after_yesterday_workout: afterYesterday,
      sleep_quality: sleep,
      stress_level: stress,
      notes: notes || null,
    });
  }

  return (
    <form className="space-y-5" onSubmit={handleSubmit}>
      <Card className="space-y-5">
        <Input
          type="date"
          name="date"
          label={t("back_checkin.field_date")}
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />

        <div>
          <SectionLabel>{t("back_checkin.field_stiffness")}</SectionLabel>
          <div className="mt-2 flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={60}
              step={5}
              value={stiffness}
              onChange={(e) => setStiffness(Number(e.target.value))}
              className="h-2 flex-1 accent-amber"
            />
            <span className="metric-number w-12 text-right text-[18px]">{stiffness}</span>
          </div>
        </div>

        <div>
          <SectionLabel>{t("back_checkin.field_pain")}</SectionLabel>
          <div className="mt-2 flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={10}
              value={pain}
              onChange={(e) => setPain(Number(e.target.value))}
              className="h-2 flex-1 accent-amber"
            />
            <span
              className={
                pain >= 5
                  ? "metric-number w-12 text-right text-[18px] text-coral"
                  : pain >= 3
                    ? "metric-number w-12 text-right text-[18px] text-amber-dark"
                    : "metric-number w-12 text-right text-[18px]"
              }
            >
              {pain}
            </span>
          </div>
        </div>

        {pain > 0 ? (
          <Input
            label={t("back_checkin.field_location")}
            placeholder={t("back_checkin.field_location_placeholder")}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <label className="flex items-center gap-2 rounded-lg border border-cream-border bg-cream-light px-3 py-2.5">
            <input
              type="checkbox"
              checked={radiating}
              onChange={(e) => setRadiating(e.target.checked)}
              className="h-4 w-4 accent-coral"
            />
            <span className="text-[13px]">{t("back_checkin.field_radiating")}</span>
          </label>
          <label className="flex items-center gap-2 rounded-lg border border-cream-border bg-cream-light px-3 py-2.5">
            <input
              type="checkbox"
              checked={numbness}
              onChange={(e) => setNumbness(e.target.checked)}
              className="h-4 w-4 accent-coral"
            />
            <span className="text-[13px]">{t("back_checkin.field_numbness")}</span>
          </label>
        </div>

        <label className="flex items-center gap-2 rounded-lg border border-cream-border bg-cream-light px-3 py-2.5">
          <input
            type="checkbox"
            checked={afterYesterday}
            onChange={(e) => setAfterYesterday(e.target.checked)}
            className="h-4 w-4 accent-amber"
          />
          <span className="text-[13px]">{t("back_checkin.field_after_yesterday")}</span>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <SectionLabel>{t("back_checkin.field_sleep")}</SectionLabel>
            <div className="mt-2 flex items-center gap-2">
              <input
                type="range"
                min={1}
                max={5}
                value={sleep}
                onChange={(e) => setSleep(Number(e.target.value))}
                className="h-2 flex-1 accent-amber"
              />
              <span className="metric-number w-6 text-right text-[16px]">{sleep}</span>
            </div>
          </div>
          <div>
            <SectionLabel>{t("back_checkin.field_stress")}</SectionLabel>
            <div className="mt-2 flex items-center gap-2">
              <input
                type="range"
                min={1}
                max={5}
                value={stress}
                onChange={(e) => setStress(Number(e.target.value))}
                className="h-2 flex-1 accent-amber"
              />
              <span className="metric-number w-6 text-right text-[16px]">{stress}</span>
            </div>
          </div>
        </div>

        <Textarea
          label={t("back_checkin.field_notes")}
          placeholder={t("back_checkin.field_notes_placeholder")}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
        />
      </Card>

      <Button type="submit" size="lg" className="w-full" loading={submitting}>
        {t("back_checkin.submit")}
      </Button>
    </form>
  );
}
